<template>
  <div class="aiw2">
    <stepper
      v-if="currentStep !== 'mode'"
      :current-step="currentStep"
      :mode="mode"
      :reachable-steps="reachableSteps"
      :frozen="navLocked"
      :setup-locked="setupLocked"
      @go="goTo"
      @locked-click="onLockedStepClick"
    />

    <welcome-back-bar
      v-if="showWelcomeBack"
      :current-step="currentStep"
      @resume="dismissWelcomeBack"
      @start-fresh="startFresh"
    />

    <div class="aiw2__body" :class="{ 'aiw2__body--sidebar': showSidebar }">
      <div class="aiw2__main">
        <!-- Mode -->
        <mode-select
          v-if="currentStep === 'mode'"
          :initial-mode="mode"
          @change="mode = $event"
          @start="onModeContinue"
        />

        <!-- Setup -->
        <template v-else-if="currentStep === 'setup'">
          <!-- Planning (setup → title): swap the form for a live progress panel so
               the wait reads as work-in-progress, not a frozen page. -->
          <div v-if="planningBusy" class="plan-progress">
            <div class="plan-progress-head">
              <h4>Planning your article</h4>
              <span class="plan-pill">{{ planPct }}%</span>
            </div>
            <div class="plan-bar-wrap"><div class="plan-bar" :style="{ width: planPct + '%' }"></div></div>
            <div class="plan-list">
              <div v-for="(s, i) in PLANNING_STAGES" :key="s.key" class="plan-row" :class="planStageStatus(i)">
                <div class="check"><span v-if="planStageStatus(i) === 'done'">✓</span><span v-else>{{ i + 1 }}</span></div>
                <div class="label">{{ s.label }}</div>
                <span v-if="planStageStatus(i) === 'active'" class="spinner"></span>
              </div>
            </div>
            <div class="plan-progress-foot">
              <span>Currently: <strong>{{ planLabel }}</strong></span>
            </div>
          </div>
          <setup-step
            v-else
            :setup="setup"
            :mode="mode"
            :keyword="keyword"
            :page-run="pageRun"
            :current-word-count="currentWordCount"
            :target-word-count="targetWordCount"
            :fetching="fetching"
            :fetch-error="fetchError"
            @change="scheduleSave"
            @fetch="onFetchUrl"
          />
        </template>

        <!-- Title -->
        <title-step
          v-else-if="currentStep === 'title'"
          ref="activeStep"
          :keyword="keyword"
          :mode="mode"
          :page-run="pageRun"
          :ai-run-id="session && session.aiRunId"
          :planning="session && session.planning"
          :initial-title="session && session.title"
          :credits="session && session.credits"
          @selected="onTitleSelected"
          @credits-changed="refreshState"
          @candidate="liveCandidate = $event"
          @bar="childBar = $event"
          @generating="titleGenerating = $event"
        />

        <!-- Draft -->
        <draft-step
          v-else-if="currentStep === 'draft'"
          ref="activeStep"
          :keyword="keyword"
          :mode="mode"
          :page-run="pageRun"
          :ai-run-id="session && session.aiRunId"
          :planning="session && session.planning"
          :setup="setup"
          :initial-structure="session && session.structure"
          :credits="session && session.credits"
          :credit-estimate="creditEstimate"
          :fast-generating="fastGenerating"
          :fast-progress="liveProgress"
          :fast-done="fastDone"
          @fast-continue="onFastDone"
          @continue="onDraftContinue"
          @credits-changed="refreshState"
          @content-changed="scoreStale = true"
          @content-scored="scoreStale = false"
          @bar="childBar = $event"
        />

        <!-- SEO & Quality -->
        <seo-quality-step
          v-else-if="currentStep === 'seo_quality'"
          ref="activeStep"
          :keyword="keyword"
          :mode="mode"
          :page-run="pageRun"
          :ai-run-id="session && session.aiRunId"
          :initial-seo-quality="seoQuality"
          :credits="session && session.credits"
          @continue="onSeoContinue"
          @analyzed="onSeoAnalyzed"
          @credits-changed="refreshState"
          @bar="childBar = $event"
        />

        <!-- Publish -->
        <publish-step
          v-else-if="currentStep === 'publish'"
          :keyword="keyword"
          :mode="mode"
          :page-run="pageRun"
          :ai-run-id="session && session.aiRunId"
          :session="session"
          :seo-quality="seoQuality"
          @switch="onSwitchRun"
          @back-to-seo="currentStep = 'seo_quality'"
        />

        <title-placeholder v-else @back="goTo('setup')" />
      </div>

      <report-sidebar
        v-if="showSidebar"
        class="aiw2__sidebar"
        :session="session"
        :setup="setup"
        :step="currentStep"
        :keyword="keyword"
        :planning="session && session.planning"
        :seo-quality="seoQuality"
        :live-candidate="liveCandidate"
        :score-stale="scoreStale"
        :scoring="scoring"
        @update-score="onUpdateScore"
      />
    </div>

    <!-- One persistent bottom bar across every step (Publish shows credits used).
         Hidden while Fast Draft runs, and on its completion screen where the
         in-panel "Review your article" CTA owns the hand-off. -->
    <bottom-bar
      v-if="!fastGenerating && !fastDone"
      :show-back="currentStep !== 'mode' && !navLocked && !!backStep"
      :estimate="barCredits"
      :balance="barBalance"
      :credits-label="barCreditsLabel"
      :own-key-provider="setup && setup.advanced && setup.advanced.api_mode === 'anthropic_key' ? 'anthropic' : (setup && setup.advanced && setup.advanced.api_mode === 'own_key' ? 'openai' : null)"
      :used="currentStep === 'publish'"
      :cta-label="barConfig.ctaLabel"
      :cta-disabled="barConfig.disabled"
      :blocking-reason="barConfig.blockingReason || ''"
      :final-note="barConfig.finalNote || ''"
      :busy="barConfig.busy"
      :hide-next="barConfig.hideNext"
      :show-prompts="$store.getters.isAdmin"
      @back="onBarBack"
      @next="onBarNext"
      @view-prompts="promptInspector.show = true"
    />

    <!-- Setup lock confirmation (design handoff) -->
    <div v-if="lockConfirm.show" class="ls-overlay" @click.self="lockConfirm.show = false">
      <div class="ls-modal" role="dialog" aria-modal="true">
        <div class="ls-head">
          <h3>Lock setup and continue?</h3>
          <button class="ls-close" aria-label="Close" @click="lockConfirm.show = false">×</button>
        </div>
        <p class="ls-body">Your setup is used to generate every title, outline and draft from here on, so it's locked once you continue — <strong>you won't be able to come back and change it.</strong></p>
        <ul class="ls-list">
          <li><span class="ico">✓</span> Keyword, location and audience are final</li>
          <li><span class="ico">✓</span> Word count, tone and content source are final</li>
          <li><span class="ico">✓</span> Internal links and add-ons are final</li>
        </ul>
        <div class="ls-cost"><span>⚡</span> To change setup later you'll need to start a new report</div>
        <div class="ls-actions">
          <button class="ls-cancel" @click="lockConfirm.show = false">Review setup</button>
          <button class="ls-confirm" :disabled="saving || planningBusy" @click="confirmLockSetup">Lock setup &amp; continue</button>
        </div>
      </div>
    </div>

    <!-- Word-count disclaimer (fires before the setup lock when a non-target count is chosen) -->
    <div v-if="wcDisclaimer.show" class="aiwm-overlay" @click.self="wcDisclaimer.show = false">
      <div class="aiwm-modal" role="dialog" aria-modal="true">
        <div class="aiwm-head">
          <h3>Before you continue</h3>
          <button class="aiwm-close" aria-label="Close" @click="wcDisclaimer.show = false">×</button>
        </div>
        <div class="aiwm-body">
          <p>We strongly advise against choosing a word count lower than the target POP suggests. Target optimization ranges and your overall optimization score are both worked out from the word count you pick.</p>
          <p style="margin-top: 10px;">If you optimize to a custom word count, the score the AI writer reports can differ from the score in your Content Brief or Extension.</p>
          <div class="aiwm-cost"><span>⚡</span><span>You selected <strong>{{ wcPickLabel }}</strong>. POP recommends <strong>{{ (targetWordCount || 0).toLocaleString() }} words</strong>.</span></div>
        </div>
        <div class="aiwm-foot">
          <button class="aiwm-btn aiwm-soft" @click="wcDisclaimer.show = false">Change word count</button>
          <button class="aiwm-btn aiwm-primary" @click="wcDisclaimerContinue">Continue anyway</button>
        </div>
      </div>
    </div>

    <!-- Review lock (fires on Continue to Publish; confirming finalizes the run) -->
    <div v-if="reviewLock.show" class="aiwm-overlay" @click.self="reviewLock.show = false">
      <div class="aiwm-modal" role="dialog" aria-modal="true">
        <div class="aiwm-head">
          <h3>Continue to Publish?</h3>
          <button class="aiwm-close" aria-label="Close" @click="reviewLock.show = false">×</button>
        </div>
        <div class="aiwm-body">
          <p>This is your last chance to change the article. Once you continue, the draft is final and <strong>you can't come back to edit or re-run fixes.</strong></p>
          <ul class="aiwm-list">
            <li><span class="ico">✓</span> Sections, headings and copy are locked</li>
            <li><span class="ico">✓</span> No more fix runs on this article</li>
            <li><span class="ico">✓</span> You can still preview and download it</li>
          </ul>
          <div class="aiwm-cost"><span>⚡</span> To change anything after this, you'll need to start a new report</div>
        </div>
        <div class="aiwm-foot">
          <button class="aiwm-btn aiwm-soft" @click="reviewLock.show = false">Keep editing</button>
          <button class="aiwm-btn aiwm-primary" @click="confirmReviewLock">Yes, continue</button>
        </div>
      </div>
    </div>

    <prompt-inspector-modal
      v-if="promptInspector.show"
      @close="promptInspector.show = false"
    />
  </div>
</template>

<script>
import "./v2.scss";

import Stepper from "./components/Stepper.vue";
import BottomBar from "./components/BottomBar.vue";
import WelcomeBackBar from "./components/WelcomeBackBar.vue";
import ReportSidebar from "./sidebar/ReportSidebar.vue";
import ModeSelect from "./steps/ModeSelect.vue";
import SetupStep from "./steps/setup/SetupStep.vue";
import TitleStep from "./steps/title/TitleStep.vue";
import DraftStep from "./steps/draft/DraftStep.vue";
import SeoQualityStep from "./steps/seo-quality/SeoQualityStep.vue";
import PublishStep from "./steps/publish/PublishStep.vue";
import TitlePlaceholder from "./steps/TitlePlaceholder.vue";
import PromptInspectorModal from "./components/PromptInspectorModal.vue";

import { MODES, STEPS, VISUAL_COMPONENTS, recommendedMax, defaultSetup } from "./constants";
import rockyScreenV2 from "./rockyScreenV2";
import {
  aiWriterCreate, aiWriterGetState, aiWriterSaveSetup, aiWriterScrapeUrl, aiWriterGetProgress,
  aiWriterPlan, aiWriterFastDraft, aiWriterRescore,
} from "@/api";

// Setup → Title planning stages, matching the backend ai_writer_pipeline_task
// progress `step` values. Drives the checklist shown while planning runs.
const PLANNING_STAGES = [
  { key: "contract", label: "Assembling your content brief" },
  { key: "term_budget", label: "Building the term budget" },
  { key: "competitors", label: "Gathering competitor insights" },
  { key: "editorial", label: "Drafting strategy & competitor insights" },
  { key: "blueprint", label: "Choosing the page blueprint" },
  { key: "research", label: "Mapping research needs" },
];

function deepMerge(base, override) {
  if (Array.isArray(base)) return Array.isArray(override) ? override : base;
  if (base && typeof base === "object") {
    const out = { ...base };
    Object.keys(override || {}).forEach((k) => {
      out[k] = deepMerge(base[k], override[k]);
    });
    return out;
  }
  return override === undefined ? base : override;
}

export default {
  name: "AiWriterV2",
  components: { Stepper, BottomBar, WelcomeBackBar, ReportSidebar, ModeSelect, SetupStep, TitleStep, DraftStep, SeoQualityStep, PublishStep, TitlePlaceholder, PromptInspectorModal },
  // Rocky readback: emits `ai-writer-screen-ready` schema-v2 snapshots (own
  // watchers on currentStep/session/setup/creditEstimate/liveProgress).
  mixins: [rockyScreenV2],
  props: {
    aiRunId: { default: null },
    pageRunId: { default: null },
    pageRun: { type: Object, default: () => ({}) },
    page: { type: Object, default: () => ({}) },
    runStrategies: { default: null },
    currentWordCount: { type: Number, default: 0 },
    targetWordCount: { type: Number, default: 0 },
    initialSession: { type: Object, default: null },
  },
  data() {
    return {
      MODES,
      PLANNING_STAGES,
      session: null,
      mode: MODES.GUIDED,
      currentStep: "mode",
      setup: defaultSetup({ targetWordCount: this.targetWordCount }),
      creditEstimate: null,
      // Draft-step "Update optimization score": stale once content is edited,
      // scoring while the re-score request is in flight.
      scoreStale: false,
      scoring: false,
      validation: null,
      seoQuality: null,
      liveCandidate: null,
      // "Lock setup and continue?" confirmation, shown when leaving Setup.
      lockConfirm: { show: false },
      // Word-count disclaimer, shown before the setup lock when the chosen count
      // isn't the POP target; review lock, shown before Publish.
      wcDisclaimer: { show: false },
      reviewLock: { show: false },
      // Token proving the review-lock was explicitly confirmed this session — the
      // seo→publish hand-off refuses to land on Publish without it (see onSeoContinue).
      publishConfirmed: false,
      creating: false,
      saving: false,
      planningBusy: false,
      // Title step is writing title variants (mirrored from TitleStep via @generating
      // so the Rocky snapshot reports loading only while that job runs).
      titleGenerating: false,
      // Fast Draft: while the one-pass generation runs we show the Draft step's
      // progress panel, driven by `liveProgress` (updated on every poll tick).
      fastGenerating: false,
      // Fast Draft finished: the Draft step keeps showing the completed checklist
      // (all stages ✓) until the user clicks through to review/publish.
      fastDone: false,
      liveProgress: null,
      fetching: false,
      fetchError: "",
      showWelcomeBack: false,
      // Bar config emitted by the active child step (title/draft/seo).
      childBar: null,
      // Admin Prompt Inspector modal (prompts accumulate in the Vuex store).
      promptInspector: { show: false },
      _saveTimer: null,
      _pollTimer: null,
      _planPollTimer: null,
    };
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    // Which planning stage the backend is on, from the live `planning` progress
    // `step`. -1 until the first tick arrives (all stages pending).
    planStageIndex() {
      const p = this.liveProgress || {};
      if (p.phase !== "planning") return -1;
      return PLANNING_STAGES.findIndex((s) => s.key === p.step);
    },
    // Steady stage-based bar so it never jumps backwards between stages; falls
    // back to the backend `pct` before the first stage is known.
    planPct() {
      const idx = this.planStageIndex;
      if (idx < 0) return (this.liveProgress && this.liveProgress.pct) || 5;
      return Math.round(((idx + 0.5) / PLANNING_STAGES.length) * 100);
    },
    planLabel() {
      const p = this.liveProgress || {};
      const stage = PLANNING_STAGES[this.planStageIndex];
      return p.label || (stage && stage.label) || "Preparing your brief…";
    },
    keyword() {
      return this.pageRun?.keyword || this.page?.targetKeyword || "";
    },
    reachableSteps() {
      // A step's chip unlocks only once the prior step is complete — no jumping
      // ahead of the current step until it's done.
      const s = this.session || {};
      const complete = {
        mode: true,
        setup: this.setupLocked, // true once past Setup
        title: this.mode === MODES.FAST || !!((((s.title || {}).selected) || {}).seo_title),
        draft: !!((s.blocksSummary || {}).generated), // draft generated_at
        seo_quality: true, // gating past SEO is the review-lock, not a chip
      };
      const out = [];
      for (const step of STEPS) {
        if (step === "publish") break; // Publish is reached only via the review-lock Continue
        out.push(step);
        if (!complete[step]) break; // stop at the first incomplete step
      }
      // The current step is always its own valid location (covers Publish + stale session data).
      if (this.currentStep && !out.includes(this.currentStep)) out.push(this.currentStep);
      return out;
    },
    // The nearest previous step the Back button can go to (skips locked Setup/Mode
    // and the Fast-mode-hidden Title). Null when there's nowhere to go back.
    backStep() {
      const order = ["mode", "setup", "title", "draft", "seo_quality", "publish"];
      const visible = order.filter((s) => (s === "title" ? !this.isFastMode : true));
      const i = visible.indexOf(this.currentStep);
      for (let k = i - 1; k >= 0; k--) {
        const s = visible[k];
        if (this.setupLocked && (s === "setup" || s === "mode")) continue;
        if (this.reachableSteps.includes(s)) return s;
      }
      return null;
    },
    // Publish ends the flow -- no navigating back to earlier steps from it.
    navLocked() {
      return this.currentStep === "publish";
    },
    // Label for the word-count disclaimer ("You selected …").
    wcPickLabel() {
      return (this.setup && this.setup.word_count_mode) === "custom"
        ? "a custom word count" : "the current word count";
    },
    // Setup is a one-way lock once the user has continued past it (Title / Draft /
    // …). Derived from the step, so a failed planning attempt (which leaves the
    // user on Setup) doesn't lock them out.
    setupLocked() {
      return STEPS.indexOf(this.currentStep) > STEPS.indexOf("setup");
    },
    isFastMode() {
      return this.mode === MODES.FAST;
    },
    // The report sidebar shows on Title/Draft/SEO only — Mode, Setup and Publish
    // are full-width (matches the design's per-step `hideSidebar`).
    showSidebar() {
      return this.currentStep !== "mode" && this.currentStep !== "setup" && this.currentStep !== "publish";
    },
    competitorsSelectionValid() {
      // Selecting 1–3 competitors is required to leave Setup.
      const sel = ((this.setup && this.setup.competitors) || []).filter((c) => c.enabled).length;
      return sel >= 1 && sel <= 3;
    },
    internalLinksValid() {
      // When internal links are enabled: top-level URL required + all provided
      // link fields must be valid http(s) URLs.
      const il = (this.setup && this.setup.internal_links) || {};
      if (!il.enabled) return true;
      const ok = (u) => {
        try { const x = new URL((u || "").trim()); return x.protocol === "http:" || x.protocol === "https:"; }
        catch (e) { return false; }
      };
      if (!ok(il.top_level_url)) return false;
      return (il.supporting_urls || []).every((u) => !(u || "").trim() || ok(u));
    },
    visualQuantityError() {
      // No component's quantity may exceed its recommended max (the upper bound
      // shown in the "Recommended" column). Returns a blocking message naming
      // the offenders, or "" when every quantity is within range.
      const comps = (this.setup && this.setup.visual_components) || {};
      const over = VISUAL_COMPONENTS.filter((c) => {
        const max = recommendedMax(c.recommended);
        if (max == null) return false;
        // Images/Lists/Tables may go up to double the recommended max (clamped
        // there in setQuantity), so they never block within range.
        const cap = c.warnable ? max * 2 : max;
        const qty = (comps[c.key] && comps[c.key].quantity) || 0;
        return qty > cap;
      });
      if (!over.length) return "";
      if (over.length === 1) {
        return `Reduce ${over[0].label} to the recommended ${recommendedMax(over[0].recommended)} or fewer to continue.`;
      }
      return `Reduce these to their recommended amount to continue: ${over.map((c) => c.label).join(", ")}.`;
    },
    // ---- Shared bottom bar -------------------------------------------------
    // Mode + Setup are driven directly here; title/draft/seo report their own
    // CTA via @bar (this.childBar). Shape: {ctaLabel, disabled, busy, hideNext}.
    barConfig() {
      if (this.currentStep === "mode") {
        return { ctaLabel: "Continue to setup →", busy: this.creating, disabled: false, hideNext: false };
      }
      if (this.currentStep === "setup") {
        const compsValid = this.competitorsSelectionValid;
        const ilValid = this.internalLinksValid;
        const qtyError = this.visualQuantityError;
        const serverMsg = this.validation && this.validation.blocking_errors && this.validation.blocking_errors.length
          ? this.validation.blocking_errors[0].message : "";
        const clientMsg = !compsValid ? "Select 1-3 competitors to continue."
          : (!ilValid ? "Add valid internal link URLs to continue." : qtyError);
        return {
          ctaLabel: this.isFastMode ? "⚡ Generate page →" : "Continue to title →",
          busy: this.saving || this.planningBusy,
          // Disable immediately (client-side) on the competitor + internal-link
          // rules and the recommended-quantity caps, plus any server validation
          // error; the proceed action re-validates server-side too.
          disabled: !!(this.validation && this.validation.can_continue === false) || !compsValid || !ilValid || !!qtyError,
          blockingReason: serverMsg || clientMsg,
          hideNext: false,
        };
      }
      if (this.currentStep === "publish") {
        // Terminal step — the export actions live in the side panel; no next
        // button here, just the final-state note.
        return { ctaLabel: "", busy: false, disabled: false, hideNext: true, finalNote: "Your article is final." };
      }
      return this.childBar || { ctaLabel: "Continue →", busy: false, disabled: true, hideNext: false };
    },
    // Show the credit-estimate cluster (total/balance/breakdown) where we have
    // a server estimate; mode/seo fall back to a plain label.
    barCredits() {
      // Mode has no run yet; SEO fixes / polish are free (no per-run estimate).
      if (this.currentStep === "mode" || this.currentStep === "seo_quality") return null;
      // Publish shows what was actually *charged* (from the backend used-breakdown),
      // NOT the pre-run required estimate — the latter pre-counts planned images the
      // user may never have generated, so it over-counts the total shown as "used".
      if (this.currentStep === "publish") return this.publishCreditsEstimate;
      // Setup / Title / Draft show the required-run estimate.
      return this.creditEstimate;
    },
    // Publish "Credits used" bar/popover, built from the backend used-breakdown
    // (`session.publish.creditsUsed`). Only actually-charged lines appear, so an
    // un-generated Images line (0 credits) never shows and never inflates the total.
    publishCreditsEstimate() {
      const used = (this.session && this.session.publish && this.session.publish.creditsUsed) || null;
      if (!used) return null;
      const lines = used.lines || [];
      const breakdown = lines
        .filter((l) => (l.credits || 0) > 0)
        .map((l) => ({ credits: l.credits, label: l.detail ? l.label + " — " + l.detail : l.label }));
      const imageLine = lines.find((l) => l.label === "Images");
      const est = this.creditEstimate || {};
      return {
        total: used.total != null ? used.total : 0,
        breakdown,
        image_credits: imageLine ? (imageLine.credits || 0) : 0,
        balance: used.balance,
        is_admin: est.is_admin,
        is_white_glove: est.is_white_glove,
      };
    },
    barCreditsLabel() {
      if (this.currentStep === "mode") return "No credits used yet";
      if (this.currentStep === "seo_quality") return "SEO fixes & polish: free";
      if (this.currentStep === "publish") {
        const used = (this.session && this.session.publish && this.session.publish.creditsUsed) || {};
        return "Credits used: " + (used.total != null ? used.total : 0);
      }
      return "";
    },
    // The live credit balance shown on the bottom bar across every step.
    // Prefer the server-computed balance from the (freshly-refreshed) session:
    // it decrements right after generation and is authoritative for sub-account /
    // white-glove owners too. Fall back to the store wallet before it's available.
    barBalance() {
      const sb = this.session && this.session.publish && this.session.publish.creditsUsed
        ? this.session.publish.creditsUsed.balance : null;
      if (sb != null) return sb;
      const u = this.$store.state.selectedSubAccountRelation || this.$store.state.user;
      if (!u) return null;
      return (u.monthlyCredits || 0) + (u.remainedUniversalCredits || 0);
    },
  },
  watch: {
    // Drop the previous step's CTA the moment we move; the new child re-emits.
    currentStep: {
      immediate: true,
      handler() {
        this.childBar = null;
      },
    },
  },
  mounted() {
    // Start each AI Writer open with a clean Prompt Inspector log.
    this.$store.commit("AIW_CLEAR_PROMPTS");
    if (this.initialSession) {
      this.adoptSession(this.initialSession);
      this.showWelcomeBack = this.currentStep && this.currentStep !== "mode";
      this.refreshState();
    } else {
      // brand-new run: seed competitors + word count from the report
      this.setup = this.buildInitialSetup();
    }
  },
  beforeDestroy() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    if (this._pollTimer) clearTimeout(this._pollTimer);
    if (this._planPollTimer) clearTimeout(this._planPollTimer);
  },
  methods: {
    // done / active / pending for the planning checklist row `i`.
    planStageStatus(i) {
      const cur = this.planStageIndex;
      if (cur < 0) return "pending";
      if (i < cur) return "done";
      if (i === cur) return "active";
      return "pending";
    },
    // Scroll the AI Writer body to the top so the just-started generation
    // progress is visible (the window doesn't scroll — the editor pane does —
    // so use scrollIntoView, not window.scrollTo).
    scrollTop() {
      this.$nextTick(() => {
        if (this.$el && this.$el.scrollIntoView) {
          this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    buildInitialSetup() {
      const s = defaultSetup({ targetWordCount: this.targetWordCount });
      const comps = (this.pageRun?.competitors || []).map((c, i) => ({
        id: c.id,
        url: c.url,
        title: c.title || "",
        source: "report",
        enabled: i < 3,
        word_count: c.wordCount || c.word_count || (c.rawKeywordCounts && c.rawKeywordCounts.word_count) || 0,
      }));
      s.competitors = comps;
      if (this.targetWordCount) s.word_count = this.targetWordCount;
      return s;
    },
    adoptSession(session) {
      this.session = session;
      this.mode = session.mode || MODES.GUIDED;
      this.currentStep = session.currentStep || "setup";
      const incoming = session.setup && Object.keys(session.setup).length ? session.setup : null;
      this.setup = incoming
        ? deepMerge(this.buildInitialSetup(), incoming)
        : this.buildInitialSetup();
      // deepMerge replaces the competitors array wholesale, so a stored setup
      // (seeded before word counts were available) drops them — re-hydrate each
      // report competitor's word_count from the live page-run competitor list.
      this.setup.competitors = this.hydrateCompetitorWordCounts(this.setup.competitors);
    },
    hydrateCompetitorWordCounts(competitors) {
      const byId = {}, byUrl = {};
      ((this.pageRun && this.pageRun.competitors) || []).forEach((c) => {
        const wc = c.wordCount || c.word_count || (c.rawKeywordCounts && c.rawKeywordCounts.word_count) || 0;
        if (c.id != null) byId[c.id] = wc;
        if (c.url) byUrl[c.url] = wc;
      });
      return (competitors || []).map((c) => ({
        ...c,
        word_count: c.word_count || byId[c.id] || byUrl[c.url] || 0,
      }));
    },
    async refreshState() {
      if (!this.session?.aiRunId && !this.aiRunId) return;
      const id = this.session?.aiRunId || this.aiRunId;
      try {
        const res = await aiWriterGetState(this.token, id);
        if (res?.data?.status === "SUCCESS") {
          this.session = res.data.session;
          if (res.data.creditEstimate) this.creditEstimate = res.data.creditEstimate;
          if (res.data.seoQuality !== undefined) this.seoQuality = res.data.seoQuality;
        }
      } catch (e) { /* ignore */ }
    },
    // "Update optimization score" (Draft step): re-score the current content, then
    // refresh the session so the sidebar shows the new score + live term coverage
    // without a page reload. Enabled only when the score is stale (content edited).
    async onUpdateScore() {
      if (!this.scoreStale || this.scoring) return;
      this.scoring = true;
      try {
        await aiWriterRescore(this.token, this.session?.aiRunId || this.aiRunId);
        await this.refreshState();
        this.scoreStale = false;
      } catch (e) {
        this.$store.commit("setGlobalErrorMsg", { msg: "Couldn't update the optimization score." });
      } finally {
        this.scoring = false;
      }
    },
    async onModeContinue(mode) {
      this.mode = mode;
      this.creating = true;
      try {
        const payload = { pageRunId: this.pageRunId, mode };
        // Reuse the run already loaded from the URL / latest instead of minting a
        // duplicate; the backend only reuses it while it's still un-started.
        const existingId = this.session && this.session.aiRunId;
        if (existingId) payload.aiRunId = existingId;
        const res = await aiWriterCreate(this.token, payload);
        if (res?.data?.status === "SUCCESS") {
          this.$store.commit("AIW_CLEAR_PROMPTS");
          this.session = res.data.session;
          this.currentStep = "setup";
          // The server seeds api_mode 'own_key' for own-key veterans (from their
          // last AI run) — adopt it BEFORE the save below, or the local default
          // ('pop', e.g. right after "Start fresh") would overwrite the seed.
          const seededMode = res.data.session?.setup?.advanced?.api_mode;
          if (seededMode && this.setup && this.setup.advanced) {
            this.setup.advanced.api_mode = seededMode;
          }
          // persist the seeded setup immediately so the server has it + an estimate
          await this.saveSetup();
        } else {
          this.$store.commit("setGlobalErrorMsg", { msg: res?.data?.error || "Failed to start." });
        }
      } catch (e) {
        this.$store.commit("setGlobalErrorMsg", { msg: "Failed to start AI Writer." });
      } finally {
        this.creating = false;
      }
    },
    scheduleSave() {
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this.saveSetup(), 600);
    },
    async saveSetup() {
      if (!this.session?.aiRunId) return;
      this.saving = true;
      try {
        const res = await aiWriterSaveSetup(this.token, this.session.aiRunId, this.setup);
        if (res?.data?.status === "SUCCESS") {
          if (res.data.session) this.session = res.data.session;
          this.creditEstimate = res.data.creditEstimate || this.creditEstimate;
          this.validation = res.data.validation || this.validation;
        }
      } catch (e) { /* ignore transient save errors */ }
      finally {
        this.saving = false;
      }
    },
    async onFetchUrl(url) {
      if (!this.session?.aiRunId || !url) return;
      this.fetching = true;
      this.fetchError = "";
      try {
        const res = await aiWriterScrapeUrl(this.token, this.session.aiRunId, url);
        if (res?.data?.status === "SUCCESS") {
          this.pollScrape();
        } else {
          this.fetching = false;
          this.fetchError = res?.data?.error || "Could not import content.";
        }
      } catch (e) {
        this.fetching = false;
        this.fetchError = "Could not import content.";
      }
    },
    pollScrape() {
      const id = this.session?.aiRunId;
      if (!id) return;
      const tick = async () => {
        try {
          const res = await aiWriterGetProgress(this.token, id);
          const progress = res?.data?.progress || {};
          if (progress.status === "complete") {
            this.fetching = false;
            await this.refreshSetupFromServer();
            return;
          }
          if (progress.status === "failed") {
            this.fetching = false;
            this.fetchError = progress.error?.message || "Could not import content.";
            return;
          }
        } catch (e) { /* keep polling */ }
        this._pollTimer = setTimeout(tick, 1500);
      };
      this._pollTimer = setTimeout(tick, 1500);
    },
    async refreshSetupFromServer() {
      const id = this.session?.aiRunId;
      if (!id) return;
      try {
        const res = await aiWriterGetState(this.token, id);
        const serverSetup = res?.data?.session?.setup;
        if (serverSetup?.content_source) {
          this.setup = deepMerge(this.setup, { content_source: serverSetup.content_source });
        }
      } catch (e) { /* ignore */ }
    },
    // Leaving Setup is a one-way lock — confirm it first ("Lock setup and
    // continue?"). If the user picked a non-target word count, warn first with the
    // word-count disclaimer, which then proceeds to the setup lock on confirm.
    continueFromSetup() {
      const mode = this.setup && this.setup.word_count_mode;
      if (mode && mode !== "recommended") {
        this.wcDisclaimer = { show: true };
        return;
      }
      this.lockConfirm = { show: true };
    },
    wcDisclaimerContinue() {
      this.wcDisclaimer.show = false;
      this.lockConfirm = { show: true };
    },
    confirmLockSetup() {
      this.lockConfirm.show = false;
      this.doLeaveSetup();
    },
    async doLeaveSetup() {
      await this.saveSetup();
      if (this.validation && this.validation.can_continue === false) return;
      if (!this.session?.aiRunId) return;
      if (this.isFastMode) return this.runFastDraft();
      this.planningBusy = true;
      try {
        const res = await aiWriterPlan(this.token, this.session.aiRunId);
        if (res?.data?.status === "SUCCESS") {
          const p = await this.pollProgress("planning");
          if (p && (p.stale || p.status === "failed")) {
            this.$store.commit("setGlobalErrorMsg", {
              msg: p.stale
                ? "Planning stalled. Please try again."
                : (p.error?.message || "Planning failed."),
            });
            return;
          }
          await this.refreshState();
          this.currentStep = "title";
        } else {
          this.$store.commit("setGlobalErrorMsg", { msg: res?.data?.error || "Planning failed." });
        }
      } catch (e) {
        this.$store.commit("setGlobalErrorMsg", { msg: "Planning failed." });
      } finally {
        this.planningBusy = false;
      }
    },
    async runFastDraft() {
      this.planningBusy = true;
      try {
        const res = await aiWriterFastDraft(this.token, this.session.aiRunId);
        // `already_running` (e.g. a double-click) => just join the live progress.
        if (res?.data?.status === "SUCCESS" || res?.data?.code === "already_running") {
          // Switch to the Draft step and show the live generation progress while
          // POP writes the whole page in one pass (matches the design).
          this.liveProgress = res.data.progress || { phase: "fast_draft", sub_step: "planning", pct: 5 };
          this.fastGenerating = true;
          this.fastDone = false;
          this.currentStep = "draft";
          this.scrollTop();
          const p = await this.pollProgress("fast_draft");
          if (p && (p.stale || p.status === "failed")) {
            this.$store.commit("setGlobalErrorMsg", {
              msg: p.stale
                ? "Generation stalled. Please try again."
                : (p.error?.message || "Generation failed."),
            });
            this.currentStep = "setup";
            return;
          }
          await this.refreshState();
          // Stay on the Draft step and surface the completed checklist (design
          // parity) — the user clicks "Review your article" to reach Publish.
          this.fastDone = true;
        } else {
          this.$store.commit("setGlobalErrorMsg", {
            msg: res?.data?.code === "insufficient_credits"
              ? "Not enough credits for a full draft."
              : (res?.data?.error || "Generation failed."),
          });
        }
      } catch (e) {
        this.$store.commit("setGlobalErrorMsg", { msg: "Generation failed." });
        this.currentStep = "setup";
      } finally {
        this.planningBusy = false;
        this.fastGenerating = false;
      }
    },
    async onSwitchRun({ id }) {
      try {
        const res = await aiWriterGetState(this.token, id);
        if (res?.data?.status === "SUCCESS") {
          this.session = res.data.session;
          if (res.data.seoQuality !== undefined) this.seoQuality = res.data.seoQuality;
          if (res.data.session?.setup) this.setup = deepMerge(this.buildInitialSetup(), res.data.session.setup);
          this.mode = res.data.session.mode || this.mode;
          this.currentStep = res.data.session.currentStep || "mode";
          // A fresh run has no title/draft, so completion-based reachableSteps
          // returns [mode, setup] on its own — the stepper re-locks automatically.
        }
      } catch (e) { /* ignore */ }
    },
    pollProgress(phase) {
      // Resolves when progress_json reports the given phase complete/failed.
      return new Promise((resolve) => {
        const id = this.session?.aiRunId;
        if (!id) return resolve(null);
        const tick = async () => {
          try {
            const res = await aiWriterGetProgress(this.token, id);
            const p = res?.data?.progress || {};
            // Publish each tick so the Draft step's Fast-Draft progress animates.
            if (p && p.phase) this.liveProgress = p;
            // ``stale`` => a hard-killed worker; stop polling so the caller can
            // offer a retry rather than waiting on a 'running' that never ends.
            if (p.stale || (p.phase === phase && (p.status === "complete" || p.status === "failed"))) {
              return resolve(p);
            }
          } catch (e) { /* keep polling */ }
          this._planPollTimer = setTimeout(tick, 1200);
        };
        this._planPollTimer = setTimeout(tick, 800);
      });
    },
    async onTitleSelected() {
      this.liveCandidate = null;
      await this.refreshState();
      this.currentStep = "draft";
    },
    async onDraftContinue() {
      await this.refreshState();
      this.currentStep = "seo_quality";
    },
    // Fast Draft: user is done reviewing the article → go to SEO & Quality (same
    // as the guided draft's "Continue to SEO & Quality"), NOT straight to Publish.
    onFastDone() {
      this.fastDone = false;
      this.currentStep = "seo_quality";
    },
    onSeoAnalyzed(sq) {
      if (sq) this.seoQuality = sq;
    },
    async onSeoContinue() {
      // Entering Publish must always pass through the review-lock confirmation.
      // If we got here without an explicit in-session confirm, re-show the modal
      // instead of silently landing on Publish.
      if (!this.publishConfirmed) { this.reviewLock = { show: true }; return; }
      this.publishConfirmed = false;
      await this.refreshState();
      this.currentStep = "publish";
    },
    goTo(step) {
      // Single choke point for step navigation — Publish is terminal.
      if (this.navLocked) return;
      // Setup (and Mode) are locked once the user has continued past Setup.
      if (this.setupLocked && (step === "setup" || step === "mode")) return;
      if (!this.reachableSteps.includes(step)) return;
      this.currentStep = step;
    },
    // Bottom-bar Next: mode/setup handled here; title/draft/seo own the action
    // (the active child exposes barNext() and advances via its existing event).
    onBarNext() {
      if (this.currentStep === "mode") return this.onModeContinue(this.mode);
      if (this.currentStep === "setup") return this.continueFromSetup();
      // Leaving SEO/Quality for Publish is the final lock — confirm it first.
      if (this.currentStep === "seo_quality") { this.reviewLock = { show: true }; return; }
      const step = this.$refs.activeStep;
      if (step && typeof step.barNext === "function") step.barNext();
    },
    confirmReviewLock() {
      this.reviewLock.show = false;
      // Grant the token that lets onSeoContinue land on Publish (consumed there).
      this.publishConfirmed = true;
      const step = this.$refs.activeStep;
      if (step && typeof step.barNext === "function") step.barNext();
    },
    // Bottom-bar Back: jump to the previous reachable step.
    onBarBack() {
      if (this.backStep) this.goTo(this.backStep);
    },
    // Clicking a locked/frozen step chip once the article is final.
    onLockedStepClick() {
      this.$notify({ group: "info", type: "warning", text: "Your article is final. Start a new report to make changes." });
    },
    dismissWelcomeBack() {
      this.showWelcomeBack = false;
    },
    startFresh() {
      this.showWelcomeBack = false;
      this.currentStep = "mode";
      this.setup = this.buildInitialSetup();
    },
    // Called by the parent (via the Dispatcher) on its "start over" flow.
    startOverAgain() {
      this.startFresh();
    },
  },
};
</script>

<style scoped>
.aiw2 { min-height: 100%; display: flex; flex-direction: column; }

/* Setup → Title planning progress panel (mirrors the Draft step's fast-mode
   generation panel so the two waits read the same). */
.plan-progress { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 20px 22px; margin-top: 8px; }
.plan-progress-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.plan-progress-head h4 { font-size: 16px; font-weight: 700; margin: 0; color: var(--text, #052e33); }
.plan-progress-head .plan-pill { margin-left: auto; background: var(--surface-2, #f7f7f7); color: var(--text-3, #6b7b7e); font-size: 11px; padding: 2px 8px; border-radius: 4px; font-weight: 600; }
.plan-bar-wrap { height: 6px; background: var(--surface-2, #f7f7f7); border-radius: 999px; overflow: hidden; margin-bottom: 14px; border: 1px solid var(--border, #e4e4e4); }
.plan-bar { height: 100%; background: linear-gradient(90deg, var(--pop-orange, #ff5700) 0%, #ff9966 100%); width: 0%; transition: width 1.1s ease-out; border-radius: 999px; }
.plan-list { display: flex; flex-direction: column; gap: 2px; }
.plan-row { display: flex; align-items: flex-start; gap: 12px; padding: 8px 0; font-size: 13.5px; }
.plan-row .check { width: 20px; height: 20px; border-radius: 50%; background: var(--pop-green, #25a97a); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.plan-row.active .check { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange, #ff5700); border: 1.5px solid var(--pop-orange, #ff5700); animation: aiw2-plan-pulse 1.6s ease-in-out infinite; }
.plan-row.pending .check { background: var(--surface-2, #f7f7f7); color: var(--text-muted, #9aa6a8); border: 1.5px solid var(--border-strong, #ccc); }
@keyframes aiw2-plan-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,87,0,.4); } 50% { box-shadow: 0 0 0 6px rgba(255,87,0,0); } }
.plan-row .label { flex: 1; font-weight: 500; color: var(--text-2, #014751); line-height: 20px; }
.plan-row.active .label { font-weight: 600; color: var(--text, #052e33); }
.plan-row.pending .label { color: var(--text-muted, #9aa6a8); }
.plan-row .spinner { width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid transparent; border-top-color: var(--pop-orange, #ff5700); border-right-color: var(--pop-orange, #ff5700); animation: aiw2-plan-spin 0.7s linear infinite; flex-shrink: 0; margin-top: 4px; }
@keyframes aiw2-plan-spin { to { transform: rotate(360deg); } }
.plan-progress-foot { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--border, #e4e4e4); font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.plan-progress-foot strong { color: var(--text, #052e33); font-weight: 600; }

/* Setup lock confirmation modal (design handoff .modal-* — prefixed `ls-` to
   dodge Bulma's global `.modal { display:none }`). */
.ls-overlay { position: fixed; inset: 0; z-index: 300; background: rgba(15,20,25,.45); display: flex; align-items: center; justify-content: center; padding: 20px; }
.ls-modal { background: var(--surface, #fff); border-radius: var(--r-xl, 14px); max-width: 460px; width: 100%; box-shadow: var(--shadow-lg, 0 24px 70px rgba(0,0,0,.28)); font-family: "Mulish", system-ui, sans-serif; }
.ls-head { padding: 20px 24px 8px; display: flex; align-items: flex-start; gap: 12px; }
.ls-head h3 { margin: 0; font-size: 17px; flex: 1; color: var(--text, #052e33); }
.ls-close { background: none; border: none; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 22px; padding: 0; line-height: 1; }
.ls-body { padding: 4px 24px 8px; margin: 6px 0; font-size: 13.5px; line-height: 1.5; color: var(--text-2, #014751); }
.ls-body strong { color: var(--text, #052e33); font-weight: 700; }
.ls-list { list-style: none; padding: 0 24px; margin: 8px 0; }
.ls-list li { padding: 6px 0; display: flex; gap: 9px; font-size: 13.5px; color: var(--text-2, #014751); }
.ls-list li .ico { color: var(--pop-green, #25a97a); font-weight: 700; margin-top: 1px; }
.ls-cost { margin: 10px 24px 4px; padding: 10px 12px; background: var(--pop-orange-soft, #ffeddd); border: 1px solid var(--pop-orange-tint, #ffd9c2); border-radius: 8px; font-size: 13px; color: var(--pop-orange-deep, #b23d00); font-weight: 600; display: flex; align-items: center; gap: 8px; }
.ls-actions { padding: 14px 24px 20px; display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid var(--border, #e4e4e4); margin-top: 12px; }
.ls-actions button { font-family: inherit; cursor: pointer; padding: 8px 16px; border-radius: 8px; font-weight: 700; font-size: 13px; border: 1.5px solid transparent; transition: all .15s; }
.ls-cancel { background: #fff; border-color: var(--border-strong, #ccc); color: var(--text, #052e33); }
.ls-cancel:hover { background: var(--surface-2, #f7f7f7); }
.ls-confirm { background: var(--pop-orange, #ff5700); color: #fff; }
.ls-confirm:hover { background: var(--pop-orange-hover, #e64e00); }
.ls-confirm:disabled { opacity: .6; cursor: not-allowed; }
/* Centered page container that matches the design's `.page` (max-width 1320,
   margin auto). The whole content+sidebar group is centered, not full-width. */
.aiw2__body { flex: 1; box-sizing: border-box; width: 100%; max-width: 1320px; margin: 0 auto; padding: 24px 24px 28px; }
/* When the report sidebar is shown, lay main + sidebar out as the design's grid:
   the main fills the 1fr column and the sidebar is the fixed right column. */
.aiw2__body--sidebar { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 24px; align-items: start; }
.aiw2__main { min-width: 0; }
.aiw2__sidebar { position: sticky; top: 24px; }
@media (max-width: 1024px) {
  .aiw2__body--sidebar { grid-template-columns: 1fr; }
  .aiw2__sidebar { position: static; }
}
</style>
